import { type CSSProperties, useCallback, useEffect, useState } from 'react';
import { usePowerSyncDb } from '../adapters/powersync';
import { useSyncStatus } from './useSyncStatus';
import { forceResync } from './syncActions';
import {
  recordRetryLog,
  recordAutoForceResync,
  canAutoForceResync,
  tryAcquireAutoForceLock,
  releaseAutoForceLock,
  AUTO_FORCE_RESYNC_POST_RELOAD_GRACE_MS,
} from './syncHealth';

/** Seconds the "Sync needs a refresh" modal waits before auto-resyncing. */
const AUTO_RESYNC_COUNTDOWN_S = 10;
// Stable per-tab id for the browser-wide auto-force lock (multi-tab coordination).
const AUTO_TAB_ID = `sdm-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Stuck-download (re-open loop) modal.
 *
 * After a sync-rules change the server bumps its checkpoint version and every
 * client must re-sync. A client whose local state can't reconcile gets caught
 * re-opening the stream forever (`Connection is busy re-opening`) and never
 * settles — the page silently shows stale data, with ZERO pending uploads so
 * StuckSyncModal (which keys off the upload queue) never fires.
 *
 * The reliable recovery is a local clear + re-sync (Force Resync). We only
 * offer it here when there are NO pending uploads, so it can't lose unsynced
 * local writes — i.e. it's non-destructive in this specific situation.
 */

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1001,
  background: 'rgba(15, 23, 42, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--sp-4, 16px)',
};

const cardStyle: CSSProperties = {
  width: '100%',
  maxWidth: 440,
  background: 'var(--bg-surface, #fff)',
  borderRadius: 'var(--radius-lg, 14px)',
  boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)',
  padding: 'var(--sp-6, 24px)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-4, 16px)',
};

const primaryBtn: CSSProperties = {
  padding: '10px 16px',
  borderRadius: 'var(--radius-md, 10px)',
  border: 'none',
  background: 'var(--primary, #2563eb)',
  color: '#fff',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 'var(--font-weight-semi, 600)',
  cursor: 'pointer',
};

const secondaryBtn: CSSProperties = {
  padding: '10px 16px',
  borderRadius: 'var(--radius-md, 10px)',
  border: '1px solid var(--border, rgba(15,23,42,0.12))',
  background: 'var(--bg-surface, #fff)',
  color: 'var(--text, #0f172a)',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 'var(--font-weight-medium, 500)',
  cursor: 'pointer',
};

const ghostBtn: CSSProperties = {
  padding: '8px 12px',
  borderRadius: 'var(--radius-md, 10px)',
  border: 'none',
  background: 'transparent',
  color: 'var(--text-muted, #64748b)',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 'var(--font-weight-medium, 500)',
  cursor: 'pointer',
};

export function StuckDownloadModal() {
  const { db } = usePowerSyncDb();
  const health = useSyncStatus();

  const [dismissed, setDismissed] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(AUTO_RESYNC_COUNTDOWN_S);

  const handleForceResync = useCallback(async () => {
    if (!db) {
      window.location.reload();
      return;
    }
    setResyncing(true);
    recordRetryLog({
      timestamp: new Date().toISOString(),
      action: 'force-resync',
      pendingBefore: health.pendingCount,
      outcome: 'started',
      detail: 'from stuck-download modal (re-open loop)',
    });
    try {
      // Clears the local DB and hard-reloads — won't return here.
      await forceResync(db);
    } catch (err) {
      console.error('[StuckDownloadModal] Force resync failed:', err);
      setResyncing(false);
      window.location.reload();
    }
  }, [db, health.pendingCount]);

  // Auto-resync fired by the countdown (no user action). Coordinated across tabs
  // by the browser-wide lock and recorded to the shared loop-guard, so it runs at
  // most once per window before reverting to a manual prompt.
  const handleAutoResync = useCallback(async () => {
    if (!db) {
      window.location.reload();
      return;
    }
    if (!tryAcquireAutoForceLock(AUTO_TAB_ID)) return; // another tab is handling it
    setResyncing(true);
    recordAutoForceResync();
    recordRetryLog({
      timestamp: new Date().toISOString(),
      action: 'auto-force-resync',
      pendingBefore: health.pendingCount,
      pendingAfter: 0,
      outcome: 'started',
      detail: 'auto: stuck-download modal countdown',
    });
    try {
      await forceResync(db);
    } catch (err) {
      console.error('[StuckDownloadModal] auto force resync failed:', err);
      releaseAutoForceLock(AUTO_TAB_ID);
      setResyncing(false);
      window.location.reload();
    }
  }, [db, health.pendingCount]);

  // Only when genuinely stuck AND there are no pending uploads — that's what
  // makes Force Resync safe (nothing local to lose). Shown on every page,
  // including Sync Status, so the prompt is never missed.
  //
  // Two triggers, both requiring an empty upload queue:
  //   - re-open loop (`downloadStuck`), or
  //   - a persistent download transport failure (`downloadFetchStuck`) that
  //     automatic recovery could NOT resolve — i.e. the per-window cap is hit
  //     AND we're past the post-reload grace (so we don't flash the modal while
  //     PowerSync is still rebuilding sync right after an auto-force reload).
  const postReloadGraceElapsed = performance.now() >= AUTO_FORCE_RESYNC_POST_RELOAD_GRACE_MS;
  const canAuto = canAutoForceResync();
  const fetchStuckExhausted = health.downloadFetchStuck && !canAuto && postReloadGraceElapsed;
  const visible =
    !dismissed && health.pendingCount === 0 && (health.downloadStuck || fetchStuckExhausted);

  // Auto-resync countdown. Runs only while an auto-force is still permitted by the
  // shared loop-guard (`canAuto`) — so it self-heals at most once, then (if still
  // stuck after the reload) the cap is hit and the modal reverts to a purely
  // manual prompt, never looping. Any button click cancels it (dismiss/refresh/
  // reload all flip `visible`/`resyncing`).
  const autoCountdownActive = visible && canAuto && !resyncing;
  useEffect(() => {
    if (!autoCountdownActive) {
      setSecondsLeft(AUTO_RESYNC_COUNTDOWN_S);
      return;
    }
    const startedAt = Date.now();
    const id = setInterval(() => {
      const left = AUTO_RESYNC_COUNTDOWN_S - Math.floor((Date.now() - startedAt) / 1000);
      if (left <= 0) {
        clearInterval(id);
        setSecondsLeft(0);
        void handleAutoResync();
      } else {
        setSecondsLeft(left);
      }
    }, 250);
    return () => clearInterval(id);
  }, [autoCountdownActive, handleAutoResync]);

  if (!visible) return null;

  return (
    <div
      style={overlayStyle}
      role="dialog"
      aria-modal="true"
      aria-labelledby="stuck-download-title"
    >
      <div style={cardStyle}>
        <h2
          id="stuck-download-title"
          style={{
            margin: 0,
            fontSize: 'var(--font-size-lg, 18px)',
            fontWeight: 'var(--font-weight-semi, 600)',
            color: 'var(--text, #0f172a)',
          }}
        >
          Sync needs a refresh
        </h2>

        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-sm, 14px)',
            lineHeight: 1.6,
            color: 'var(--text-muted, #475569)',
          }}
        >
          This device is stuck reconnecting and can’t catch up to the latest data, so what you see
          may be out of date. There are no unsaved changes waiting on this device, so it’s safe to
          refresh the local copy — this clears it and re-downloads everything from the server.
        </p>

        {autoCountdownActive && (
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-sm, 14px)',
              fontWeight: 'var(--font-weight-medium, 500)',
              color: 'var(--text, #0f172a)',
            }}
            aria-live="polite"
          >
            Refreshing automatically in {secondsLeft}s — or choose an option below.
          </p>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3, 12px)' }}>
          <button type="button" onClick={handleForceResync} disabled={resyncing} style={primaryBtn}>
            {resyncing
              ? 'Refreshing…'
              : autoCountdownActive
                ? `Refresh data (${secondsLeft})`
                : 'Refresh data'}
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            disabled={resyncing}
            style={secondaryBtn}
          >
            Reload page
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={() => setDismissed(true)} style={ghostBtn}>
            Dismiss for now
          </button>
        </div>
      </div>
    </div>
  );
}
